import axios from 'axios';
import { executeRecaptcha } from './recaptcha';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

// Attach token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('calibza_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 globally
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('calibza_token');
      localStorage.removeItem('calibza_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// login / register / forgot auto-attach a v3 reCAPTCHA token;
// backend's enforceRecaptcha picks it up. Returns null when not configured.
const withRecaptcha = async (action, data) => {
  const recaptcha_token = await executeRecaptcha(action);
  return { ...data, recaptcha_token };
};

// ── Auth ──────────────────────────────────────────────────────
export const authApi = {
  login:     async (data) => api.post('/auth/login',    await withRecaptcha('login',           data)),
  register:  async (data) => api.post('/auth/register', await withRecaptcha('register',        data)),
  verifyOtp: (data)       => api.post('/auth/verify-otp', data),
  resendOtp: (data)       => api.post('/auth/resend-otp', data),
  logout:    ()           => api.post('/auth/logout'),
  me:        ()           => api.get('/auth/me'),
  forgot:    async (data) => api.post('/auth/forgot',   await withRecaptcha('forgot_password', data)),
  reset:     (data)       => api.post('/auth/reset', data),
};

// ── Employees ─────────────────────────────────────────────────
export const employeeApi = {
  list:    (params) => api.get('/employees', { params }),
  get:     (id)     => api.get(`/employees/${id}`),
  create:  (data)   => api.post('/employees', data),
  update:  (id, d)  => api.patch(`/employees/${id}`, d),
  delete:  (id)     => api.delete(`/employees/${id}`),
  scores:  (id)     => api.get(`/employees/${id}/scores`),
  history: (id)     => api.get(`/employees/${id}/history`),
  feedback:(id)     => api.get(`/employees/${id}/feedback`),
  import:  (form)   => api.post('/employees/import', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  sync:    ()       => api.post('/employees/sync'),
};

// ── Departments ───────────────────────────────────────────────
export const departmentApi = {
  list:   (params) => api.get('/departments', { params }),
  get:    (id)     => api.get(`/departments/${id}`),
  create: (data)   => api.post('/departments', data),
  update: (id, d)  => api.patch(`/departments/${id}`, d),
  delete: (id)     => api.delete(`/departments/${id}`),
};

// ── Cycles ────────────────────────────────────────────────────
export const cycleApi = {
  list:           (params) => api.get('/cycles', { params }),
  get:            (id)     => api.get(`/cycles/${id}`),
  create:         (data)   => api.post('/cycles', data),
  update:         (id, d)  => api.patch(`/cycles/${id}`, d),
  delete:         (id)     => api.delete(`/cycles/${id}`),
  activate:       (id)     => api.post(`/cycles/${id}/activate`),
  close:          (id)     => api.post(`/cycles/${id}/close`),
  triggerScoring: (id)     => api.post(`/cycles/${id}/score`),
  summary:        (id)     => api.get(`/cycles/${id}/summary`),
  export:         (id)     => api.get(`/cycles/${id}/export`, { responseType: 'blob' }),
};

// ── Reviews ───────────────────────────────────────────────────
export const reviewApi = {
  list:      (params)  => api.get('/reviews', { params }),
  byCycle:   (cid, p)  => api.get(`/cycles/${cid}/reviews`, { params: p }),
  get:       (id)      => api.get(`/reviews/${id}`),
  create:    (data)    => api.post('/reviews', data),
  update:    (id, d)   => api.patch(`/reviews/${id}`, d),
  submit:    (id, d)   => api.post(`/reviews/${id}/submit`, d),
  myPending: ()        => api.get('/my/pending-reviews'),
  templates: {
    list:   (p)     => api.get('/review-templates', { params: p }),
    get:    (id)    => api.get(`/review-templates/${id}`),
    create: (data)  => api.post('/review-templates', data),
    update: (id, d) => api.patch(`/review-templates/${id}`, d),
    delete: (id)    => api.delete(`/review-templates/${id}`),
  },
};

// ── Scores & Ratings ─────────────────────────────────────────
export const scoreApi = {
  byCycle:   (cid)      => api.get(`/cycles/${cid}/scores`),
  getForEmp: (eid, cid) => api.get(`/employees/${eid}/scores/${cid}`),
};

export const ratingApi = {
  byCycle:     (cid)          => api.get(`/cycles/${cid}/ratings`),
  override:    (id, data)     => api.patch(`/ratings/${id}/override`, data),
  setModifier: (id, data)     => api.patch(`/ratings/${id}/modifier`, data),
  countersign: (id, data)     => api.post (`/ratings/${id}/countersign`, data),
  lock:        (cid)          => api.post (`/cycles/${cid}/ratings/lock`),
  publish:     (cid)          => api.post (`/cycles/${cid}/ratings/publish`),
};

// ── Calibration ───────────────────────────────────────────────
export const calibrationApi = {
  board:         (cid, p)    => api.get (`/cycles/${cid}/calibration`, { params: p }),
  distribution:  (cid)       => api.get (`/cycles/${cid}/calibration/distribution`),
  flags:         (cid)       => api.get (`/cycles/${cid}/calibration/flags`),
  nineBox:       (cid)       => api.get (`/cycles/${cid}/ninebox`),
  createSession: (cid, data) => api.post(`/cycles/${cid}/calibration/sessions`, data),
};

// ── PIPs ──────────────────────────────────────────────────────
export const pipApi = {
  list:       (p)         => api.get (  '/pips', { params: p }),
  get:        (id)        => api.get (  `/pips/${id}`),
  create:     (data)      => api.post(  '/pips', data),
  update:     (id, d)     => api.patch( `/pips/${id}`, d),
  addGoal:    (id, data)  => api.post(  `/pips/${id}/goals`, data),
  updateGoal: (id, gid, d)=> api.patch( `/pips/${id}/goals/${gid}`, d),
  checkIn:    (id, data)  => api.post(  `/pips/${id}/checkin`, data),
  close:      (id, data)  => api.patch( `/pips/${id}/close`, data),
};

// ── Goals / OKRs ──────────────────────────────────────────────
export const goalApi = {
  list:            (p)          => api.get (  '/goals', { params: p }),
  get:             (id)         => api.get (  `/goals/${id}`),
  create:          (data)       => api.post(  '/goals', data),
  update:          (id, d)      => api.patch( `/goals/${id}`, d),
  delete:          (id)         => api.delete(`/goals/${id}`),
  addKeyResult:    (id, data)   => api.post(  `/goals/${id}/key-results`, data),
  updateKeyResult: (id, krid, d)=> api.patch( `/goals/${id}/key-results/${krid}`, d),
  byDepartment:    (did)        => api.get (  `/departments/${did}/goals`),
  heatmap:         (p)          => api.get (  '/goals/health/heatmap', { params: p }),
};

// ── Feedback ──────────────────────────────────────────────────
export const feedbackApi = {
  list:    (p)     => api.get (  '/feedback', { params: p }),
  get:     (id)    => api.get (  `/feedback/${id}`),
  create:  (data)  => api.post(  '/feedback', data),
  update:  (id, d) => api.patch( `/feedback/${id}`, d),
  delete:  (id)    => api.delete(`/feedback/${id}`),
  request: (data)  => api.post(  '/feedback/request', data),
};

// ── Surveys ───────────────────────────────────────────────────
export const surveyApi = {
  list:    (p)        => api.get (  '/surveys', { params: p }),
  get:     (id)       => api.get (  `/surveys/${id}`),
  create:  (data)     => api.post(  '/surveys', data),
  update:  (id, d)    => api.patch( `/surveys/${id}`, d),
  delete:  (id)       => api.delete(`/surveys/${id}`),
  launch:  (id)       => api.post(  `/surveys/${id}/launch`),
  respond: (id, data) => api.post(  `/surveys/${id}/respond`, data),
  results: (id)       => api.get (  `/surveys/${id}/results`),
  heatmap: (id)       => api.get (  `/surveys/${id}/heatmap`),
};

// ── Check-ins ─────────────────────────────────────────────────
export const checkInApi = {
  list:   (p)     => api.get (  '/checkins', { params: p }),
  get:    (id)    => api.get (  `/checkins/${id}`),
  create: (data)  => api.post(  '/checkins', data),
  update: (id, d) => api.patch( `/checkins/${id}`, d),
  delete: (id)    => api.delete(`/checkins/${id}`),
};

// ── 1:1 Meetings ──────────────────────────────────────────────
export const meetingApi = {
  list:       (p)         => api.get (  '/meetings', { params: p }),
  get:        (id)        => api.get (  `/meetings/${id}`),
  create:     (data)      => api.post(  '/meetings', data),
  update:     (id, d)     => api.patch( `/meetings/${id}`, d),
  delete:     (id)        => api.delete(`/meetings/${id}`),
  addItem:    (id, data)  => api.post(  `/meetings/${id}/items`, data),
  updateItem: (id, iid, d)=> api.patch( `/meetings/${id}/items/${iid}`, d),
  complete:   (id, data)  => api.post(  `/meetings/${id}/complete`, data),
};

// ── Compensation ──────────────────────────────────────────────
export const compensationApi = {
  bands:      (p)       => api.get (  '/salary-bands', { params: p }),
  getBand:    (id)      => api.get (  `/salary-bands/${id}`),
  createBand: (data)    => api.post(  '/salary-bands', data),
  updateBand: (id, d)   => api.patch( `/salary-bands/${id}`, d),
  deleteBand: (id)      => api.delete(`/salary-bands/${id}`),
  meritCycle: (cid)     => api.get (  `/cycles/${cid}/compensation`),
  update:     (eid, d)  => api.patch( `/employees/${eid}/compensation`, d),
};

// ── Succession ────────────────────────────────────────────────
export const successionApi = {
  list:          (p)      => api.get (  '/succession', { params: p }),
  create:        (data)   => api.post(  '/succession', data),
  update:        (id, d)  => api.patch( `/succession/${id}`, d),
  benchStrength: ()       => api.get (  '/succession/bench-strength'),
  skillMatches:  (lid)    => api.get (  `/succession/skill-matches/${lid}`),
};

// ── Employee Skills ───────────────────────────────────────────
export const skillApi = {
  listForEmployee: (eid, p)      => api.get (  `/employees/${eid}/skills`, { params: p }),
  add:             (eid, data)   => api.post(  `/employees/${eid}/skills`, data),
  update:          (eid, sid, d) => api.patch( `/employees/${eid}/skills/${sid}`, d),
  remove:          (eid, sid)    => api.delete(`/employees/${eid}/skills/${sid}`),
  endorse:         (eid, sid)    => api.post(  `/employees/${eid}/skills/${sid}/endorse`),
};

// ── Job Board ─────────────────────────────────────────────────
export const jobBoardApi = {
  list:               (p)        => api.get (  '/job-postings', { params: p }),
  get:                (id)       => api.get (  `/job-postings/${id}`),
  create:             (data)     => api.post(  '/job-postings', data),
  update:             (id, d)    => api.patch( `/job-postings/${id}`, d),
  delete:             (id)       => api.delete(`/job-postings/${id}`),
  open:               (id)       => api.post(  `/job-postings/${id}/open`),
  close:              (id)       => api.post(  `/job-postings/${id}/close`),
  fill:               (id, data) => api.post(  `/job-postings/${id}/fill`, data),
  applicants:         (id)       => api.get (  `/job-postings/${id}/applicants`),
  myMatches:          ()         => api.get (  '/my/job-matches'),
  apply:              (id, data) => api.post(  `/job-postings/${id}/applications`, data),
  applications:       (p)        => api.get (  '/job-applications', { params: p }),
  applicationById:    (id)       => api.get (  `/job-applications/${id}`),
  updateStatus:       (id, data) => api.patch( `/job-applications/${id}/status`, data),
  withdrawApplication:(id)       => api.post(  `/job-applications/${id}/withdraw`),
};

// ── Integrations ──────────────────────────────────────────────
export const integrationApi = {
  list:       ()           => api.get (  '/integrations'),
  connect:    (type, data) => api.post(  `/integrations/${type}/connect`, data),
  sync:       (type)       => api.post(  `/integrations/${type}/sync`),
  disconnect: (type)       => api.delete(`/integrations/${type}`),
  ingest:     (data)       => api.post(  '/integrations/ingest', data),
};

// ── Webhooks ──────────────────────────────────────────────────
export const webhookApi = {
  list:       (p)     => api.get (  '/webhooks', { params: p }),
  get:        (id)    => api.get (  `/webhooks/${id}`),
  create:     (data)  => api.post(  '/webhooks', data),
  update:     (id, d) => api.patch( `/webhooks/${id}`, d),
  delete:     (id)    => api.delete(`/webhooks/${id}`),
  test:       (id)    => api.post(  `/webhooks/${id}/test`),
  deliveries: (id)    => api.get (  `/webhooks/${id}/deliveries`),
};

// ── Data Health ───────────────────────────────────────────────
export const dataHealthApi = {
  overview: () => api.get('/data-health'),
};

// ── Analytics ─────────────────────────────────────────────────
export const analyticsApi = {
  overview:             (p) => api.get('/analytics/overview', { params: p }),
  distribution:         (p) => api.get('/analytics/distribution', { params: p }),
  attritionRisk:        (p) => api.get('/analytics/attrition-risk', { params: p }),
  managerEffectiveness: (p) => api.get('/analytics/manager-effectiveness', { params: p }),
  teamHealth:           (p) => api.get('/analytics/team-health', { params: p }),
  engagementTrends:     (p) => api.get('/analytics/engagement-trends', { params: p }),
};

// ── Config ────────────────────────────────────────────────────
export const configApi = {
  get:                () => api.get('/config'),
  update:             (d)=> api.put('/config', d),
  updateWeights:      (d)=> api.put('/config/weights', d),
  updateDistribution: (d)=> api.put('/config/distribution', d),
};

// ── Learning: Courses ─────────────────────────────────────────
export const courseApi = {
  list:             (p)         => api.get (  '/courses', { params: p }),
  get:              (id)        => api.get (  `/courses/${id}`),
  create:           (data)      => api.post(  '/courses', data),
  update:           (id, d)     => api.patch( `/courses/${id}`, d),
  delete:           (id)        => api.delete(`/courses/${id}`),
  enroll:           (id, data)  => api.post(  `/courses/${id}/enroll`, data ?? {}),
  markComplete:     (eid, data) => api.post(  `/enrollments/${eid}/complete`, data ?? {}),
  mine:             ()          => api.get (  '/my/enrollments'),
  mandatoryStatus:  ()          => api.get (  '/learning/mandatory'),
};

// ── Learning: Paths ───────────────────────────────────────────
export const learningPathApi = {
  list:        (p)            => api.get (  '/learning-paths', { params: p }),
  get:         (id)           => api.get (  `/learning-paths/${id}`),
  create:      (data)         => api.post(  '/learning-paths', data),
  update:      (id, d)        => api.patch( `/learning-paths/${id}`, d),
  delete:      (id)           => api.delete(`/learning-paths/${id}`),
  addItem:     (id, data)     => api.post(  `/learning-paths/${id}/items`, data),
  removeItem:  (id, itemId)   => api.delete(`/learning-paths/${id}/items/${itemId}`),
  assign:      (id, data)     => api.post(  `/learning-paths/${id}/assign`, data),
  mine:        ()             => api.get (  '/my/learning-paths'),
};

// ── Learning: IDPs ────────────────────────────────────────────
export const idpApi = {
  list:                    (p)          => api.get (  '/idps', { params: p }),
  get:                     (id)         => api.get (  `/idps/${id}`),
  create:                  (data)       => api.post(  '/idps', data),
  update:                  (id, d)      => api.patch( `/idps/${id}`, d),
  delete:                  (id)         => api.delete(`/idps/${id}`),
  endorse:                 (id)         => api.post(  `/idps/${id}/endorse`),
  generateRecommendations: (id)         => api.post(  `/idps/${id}/recommendations`),
  mine:                    ()           => api.get (  '/my/idp'),
};

// ── Audit Log ─────────────────────────────────────────────────
export const auditApi = {
  list: (p) => api.get('/audit-logs', { params: p }),
};

// ── Time Off ──────────────────────────────────────────────────
export const timeOffApi = {
  list:     (p)      => api.get('/time-off', { params: p }),
  create:   (data)   => api.post('/time-off', data),
  get:      (id)     => api.get(`/time-off/${id}`),
  approve:  (id, d)  => api.patch(`/time-off/${id}`, { ...(d || {}), status: 'approved' }),
  reject:   (id, d)  => api.patch(`/time-off/${id}`, { ...(d || {}), status: 'rejected' }),
  cancel:   (id, d)  => api.patch(`/time-off/${id}`, { ...(d || {}), status: 'cancelled' }),
  policies: {
    list:   (p)      => api.get('/time-off-policies', { params: p }),
    create: (data)   => api.post('/time-off-policies', data),
    update: (id, d)  => api.patch(`/time-off-policies/${id}`, d),
    delete: (id)     => api.delete(`/time-off-policies/${id}`),
  },
  balances: (eid, p) => api.get(`/leave-balances/${eid}`, { params: p }),
};

// ── Documents ─────────────────────────────────────────────────
export const documentApi = {
  list:       (p)       => api.get('/documents', { params: p }),
  upload:     (form)    => api.post('/documents', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  get:        (id)      => api.get(`/documents/${id}`),
  delete:     (id)      => api.delete(`/documents/${id}`),
  sign:       (id, d)   => api.post(`/documents/${id}/sign`, d),
  signatures: (id)      => api.get(`/documents/${id}/signatures`),
};

// ── Onboarding ────────────────────────────────────────────────
export const onboardingApi = {
  plans: {
    list:       (p)           => api.get('/onboarding-plans', { params: p }),
    get:        (id)          => api.get(`/onboarding-plans/${id}`),
    create:     (data)        => api.post('/onboarding-plans', data),
    update:     (id, d)       => api.patch(`/onboarding-plans/${id}`, d),
    destroy:    (id)          => api.delete(`/onboarding-plans/${id}`),
    addTask:    (id, data)    => api.post(`/onboarding-plans/${id}/tasks`, data),
    updateTask: (tid, data)   => api.patch(`/onboarding-tasks/${tid}`, data),
    deleteTask: (tid)         => api.delete(`/onboarding-tasks/${tid}`),
  },
  assign:     (eid, planId, data) => api.post(`/employees/${eid}/onboarding`, { plan_id: planId, ...(data || {}) }),
  status:     (eid)               => api.get(`/employees/${eid}/onboarding`),
  markTask:   (tid, data)         => api.post(`/onboarding-tasks/${tid}/complete`, data),
  unmarkTask: (tid, data)         => api.delete(`/onboarding-tasks/${tid}/complete`, { data }),
};

// ── Offboarding ───────────────────────────────────────────────
export const offboardingApi = {
  plans: {
    list:       (p)           => api.get('/offboarding-plans', { params: p }),
    get:        (id)          => api.get(`/offboarding-plans/${id}`),
    create:     (data)        => api.post('/offboarding-plans', data),
    update:     (id, d)       => api.patch(`/offboarding-plans/${id}`, d),
    destroy:    (id)          => api.delete(`/offboarding-plans/${id}`),
    addTask:    (id, data)    => api.post(`/offboarding-plans/${id}/tasks`, data),
    updateTask: (tid, data)   => api.patch(`/offboarding-tasks/${tid}`, data),
    deleteTask: (tid)         => api.delete(`/offboarding-tasks/${tid}`),
  },
  assign:     (eid, planId, data) => api.post(`/employees/${eid}/offboarding`, { plan_id: planId, ...(data || {}) }),
  status:     (eid)               => api.get(`/employees/${eid}/offboarding`),
  markTask:   (tid, data)         => api.post(`/offboarding-tasks/${tid}/complete`, data),
  unmarkTask: (tid, data)         => api.delete(`/offboarding-tasks/${tid}/complete`, { data }),
};

// ── AI Intelligence Layer ─────────────────────────────────────
export const aiApi = {
  attritionRiskAll:          (p)        => api.get('/ai/attrition-risk', { params: p }),
  attritionRisk:             (eid)      => api.get(`/ai/attrition-risk/${eid}`),
  promotionReadinessAll:     (p)        => api.get('/ai/promotion-readiness', { params: p }),
  promotionReadiness:        (eid)      => api.get(`/ai/promotion-readiness/${eid}`),
  managerEffectivenessAll:   ()         => api.get('/ai/manager-effectiveness'),
  managerEffectiveness:      (eid)      => api.get(`/ai/manager-effectiveness/${eid}`),
  teamHealthAll:             ()         => api.get('/ai/team-health'),
  teamHealth:                (did)      => api.get(`/ai/team-health/${did}`),
  compAnomalies:             ()         => api.get('/ai/comp-anomalies'),
  biasAudits:                ()         => api.get('/ai/bias-audits'),
  biasAudit:                 (id)       => api.get(`/ai/bias-audits/${id}`),
  runBiasAudit:              (cid)      => api.post(`/ai/bias-audits/cycles/${cid}`),
  calibrationSuggestions:    (cid)      => api.get(`/ai/calibration-suggestions/${cid}`),
  draftReview:               (data)     => api.post('/ai/draft-review', data),
  reviewSentiment:           (rid)      => api.get(`/ai/reviews/${rid}/sentiment`),
  biasCheck:                 (text)     => api.post('/ai/bias-check', { text }),
  refresh:                   ()         => api.post('/ai/refresh'),
};

// ── Benchmarks ────────────────────────────────────────────────
export const benchmarkApi = {
  settings:       ()        => api.get('/benchmarks/settings'),
  updateSettings: (data)    => api.patch('/benchmarks/settings', data),
  comparison:     (segment) => api.get('/benchmarks/comparison', { params: { segment } }),
  refresh:        ()        => api.post('/benchmarks/refresh'),
};

// ── Workforce Planning ────────────────────────────────────────
export const workforceApi = {
  forecasts: (params) => api.get('/workforce/forecasts', { params }),
  summary:   (horizon) => api.get('/workforce/summary', { params: { horizon } }),
  refresh:   (data)   => api.post('/workforce/refresh', data),
};

// ── Payroll ───────────────────────────────────────────────────
export const payrollApi = {
  periods:           (params) => api.get('/payroll/periods', { params }),
  createPeriod:      (data)   => api.post('/payroll/periods', data),
  period:            (id)     => api.get(`/payroll/periods/${id}`),
  deletePeriod:      (id)     => api.delete(`/payroll/periods/${id}`),
  generateRuns:      (id)     => api.post(`/payroll/periods/${id}/generate`),
  approve:           (id)     => api.post(`/payroll/periods/${id}/approve`),
  exportPeriod:      (id, format) => api.post(`/payroll/periods/${id}/export`, { format }, { responseType: 'blob' }),
  updateRun:         (id, data)   => api.patch(`/payroll/runs/${id}`, data),
  addAdjustment:     (id, data)   => api.post(`/payroll/runs/${id}/adjustments`, data),
  deleteAdjustment:  (id)         => api.delete(`/payroll/adjustments/${id}`),
};

// ── Global Compliance ─────────────────────────────────────────
export const complianceApi = {
  countryLibrary:      ()        => api.get('/time-off-policies/country-library'),
  seedCountryDefaults: (country) => api.post('/time-off-policies/seed-country-defaults', null, { params: country ? { country } : {} }),
};

// ── Payroll Engine (W-4, deductions, ACH, stubs) ─────────────
export const payrollEngineApi = {
  // Employee settings (HR or employee self-service)
  getTaxProfile:    (empId)            => api.get(`/employees/${empId}/tax-profile`),
  updateTaxProfile: (empId, data)      => api.patch(`/employees/${empId}/tax-profile`, data),
  listDeductions:   (empId)            => api.get(`/employees/${empId}/deductions`),
  addDeduction:     (empId, data)      => api.post(`/employees/${empId}/deductions`, data),
  updateDeduction:  (id, data)         => api.patch(`/deductions/${id}`, data),
  deleteDeduction:  (id)               => api.delete(`/deductions/${id}`),
  listBankAccounts: (empId)            => api.get(`/employees/${empId}/bank-accounts`),
  addBankAccount:   (empId, data)      => api.post(`/employees/${empId}/bank-accounts`, data),
  deleteBankAccount:(id)               => api.delete(`/bank-accounts/${id}`),

  // Engine / HR admin
  runEngine:        (periodId, data)   => api.post(`/payroll/periods/${periodId}/run-engine`, data),
  preview:          (periodId, empId)  => api.get(`/payroll/periods/${periodId}/preview/${empId}`),
  summary:          (periodId)         => api.get(`/payroll/periods/${periodId}/engine-summary`),
  downloadAch:      (periodId, originator) => api.post(`/payroll/periods/${periodId}/ach`, originator, { responseType: 'blob' }),
  getStub:          (stubId)           => api.get(`/pay-stubs/${stubId}`),

  // Employee self-service
  myStubs:          ()                 => api.get('/my/pay-stubs'),
};

export default api;
